import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipState {
  text: string;
  x: number;
  y: number;
  visible: boolean;
  align: 'left' | 'center' | 'right';
}

const OFFSET_BELOW = 8; // px gap below the element
const SHOW_DELAY = 2000;
const AUTO_HIDE_DELAY = 2000;
const FADE_DURATION = 300;

const EMPTY: TooltipState = { text: '', x: 0, y: 0, visible: false, align: 'center' };

export const GlobalTooltip: React.FC = () =>
{
  const [tooltip, setTooltip] = useState<TooltipState>(EMPTY);
  const [opacity, setOpacity] = useState(1);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoHideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () =>
  {
    if (showDelayRef.current)
    {
      clearTimeout(showDelayRef.current);
      showDelayRef.current = null;
    }
    if (autoHideRef.current)
    {
      clearTimeout(autoHideRef.current);
      autoHideRef.current = null;
    }
    if (fadeRef.current)
    {
      clearTimeout(fadeRef.current);
      fadeRef.current = null;
    }
  };

  const hide = () =>
  {
    clearTimers();
    setTooltip(EMPTY);
    setOpacity(1);
  };

  useEffect(() =>
  {
    const getTooltipText = (el: HTMLElement): string =>
      el.getAttribute('ht-name') || el.dataset.title || '';

    const findTarget = (el: HTMLElement | null): HTMLElement | null =>
    {
      while (el && el !== document.body)
      {
        if (el.classList.contains('enable-help-tooltip')) return el;
        el = el.parentElement;
      }
      return null;
    };

    const onMouseOver = (e: MouseEvent) =>
    {
      const target = findTarget(e.target as HTMLElement);
      if (!target) return;
      const text = getTooltipText(target);
      if (!text) return;

      clearTimers();

      const rect = target.getBoundingClientRect();
      const tooltipWidth = tooltipRef.current?.offsetWidth || 180;

      // Y: always below the element
      const y = rect.bottom + OFFSET_BELOW;

      // X alignment — mirrors legacy left/center/right logic
      let x: number;
      let align: TooltipState['align'];

      const elementLeft = rect.left;

      if (target.classList.contains('right'))
      {
        // element has explicit 'right' class → right-align tooltip to element
        x = elementLeft + rect.width - tooltipWidth;
        align = 'right';
      }
      else if (elementLeft <= 250)
      {
        x = elementLeft;
        align = 'left';
      }
      else if (elementLeft >= window.innerWidth - 250)
      {
        x = elementLeft - tooltipWidth + rect.width / 2 + 16;
        align = 'right';
      }
      else
      {
        x = elementLeft - tooltipWidth / 2 + rect.width / 2;
        align = 'center';
      }

      // Show after 3s delay, then auto-hide after 2s with fade
      showDelayRef.current = setTimeout(() =>
      {
        setOpacity(1);
        setTooltip({ text, x, y, visible: true, align });

        autoHideRef.current = setTimeout(() =>
        {
          setOpacity(0);
          fadeRef.current = setTimeout(hide, FADE_DURATION);
        }, AUTO_HIDE_DELAY);
      }, SHOW_DELAY);
    };

    const onMouseOut = (e: MouseEvent) =>
    {
      const target = findTarget(e.target as HTMLElement);
      if (!target) return;
      hide();
    };

    const onScroll = () => hide();

    document.addEventListener('mouseover', onMouseOver);
    document.addEventListener('mouseout', onMouseOut);
    document.addEventListener('scroll', onScroll, true);

    return () =>
    {
      document.removeEventListener('mouseover', onMouseOver);
      document.removeEventListener('mouseout', onMouseOut);
      document.removeEventListener('scroll', onScroll, true);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!tooltip.visible) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      style={{
        position: 'fixed',
        top: tooltip.y,
        left: tooltip.x,
        backgroundColor: '#e6faff',
        color: '#666',
        padding: '2px 8px',
        fontSize: '9px',
        borderRadius: '4px',
        boxShadow: '0 0 3px 0 rgba(0, 0, 0, 0.1)',
        maxWidth: '180px',
        textAlign: 'center',
        overflowWrap: 'break-word',
        zIndex: 999999999,
        pointerEvents: 'none',
        opacity,
        transition: `opacity ${FADE_DURATION}ms ease`,
      }}
    >
      {tooltip.text}
    </div>,
    document.body,
  );
};
